import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "../components/ui/use-toast";
import { fetch } from "./fetch";
import { BookingAddendumState, BookingContractStatus } from "../app/Bookings/model";

/** Mutations for the staged contract signature + addendum records
 *  (workspace integration Phase B). All invalidate the booking query the
 *  details page reads, matching useBookingContractSignedMutation. */

const useBookingInvalidate = (bookingId: string) => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ["bookingYachtOfferData", bookingId] });
};

export const useBookingContractStatusMutation = (bookingId: string) => {
  const invalidate = useBookingInvalidate(bookingId);
  const { toast } = useToast();

  return useMutation({
    mutationFn: (contractStatus: BookingContractStatus) =>
      fetch(`/api/bookings/${bookingId}/contract-status`, "PATCH", { contractStatus }),
    onSuccess: () => {
      toast({ title: "Success", description: "Contract signature stage updated." });
      invalidate();
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update contract stage.",
        variant: "destructive",
      });
    },
  });
};

export type CreateAddendumBody = {
  description?: string | null;
  effect?: string | null;
  fileId?: string | null;
};

export const useCreateBookingAddendumMutation = (bookingId: string) => {
  const invalidate = useBookingInvalidate(bookingId);
  const { toast } = useToast();

  return useMutation({
    mutationFn: (body: CreateAddendumBody) => fetch(`/api/bookings/${bookingId}/addendums`, "POST", body),
    onSuccess: () => {
      toast({ title: "Success", description: "Addendum created." });
      invalidate();
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to create addendum.", variant: "destructive" });
    },
  });
};

export type UpdateAddendumBody = {
  addendumId: string;
  description?: string | null;
  effect?: string | null;
  state?: BookingAddendumState;
  signedDate?: string | null;
  fileId?: string | null;
};

export const useUpdateBookingAddendumMutation = (bookingId: string) => {
  const invalidate = useBookingInvalidate(bookingId);
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ addendumId, ...body }: UpdateAddendumBody) =>
      fetch(`/api/bookings/${bookingId}/addendums/${addendumId}`, "PATCH", body),
    onSuccess: () => {
      toast({ title: "Success", description: "Addendum updated." });
      invalidate();
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to update addendum.", variant: "destructive" });
    },
  });
};

export const useDeleteBookingAddendumMutation = (bookingId: string) => {
  const invalidate = useBookingInvalidate(bookingId);
  const { toast } = useToast();

  return useMutation({
    mutationFn: (addendumId: string) => fetch(`/api/bookings/${bookingId}/addendums/${addendumId}`, "DELETE"),
    onSuccess: () => {
      toast({ title: "Success", description: "Addendum removed." });
      invalidate();
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to remove addendum.", variant: "destructive" });
    },
  });
};
